import React from "react";
import { Text, VStack } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { KYDInput, KYDMultiSelect } from "src/ui";
import { KYDGenresList } from "src/Manager/Genres";
import { InstantSearch } from "react-instantsearch";
import AlgoliaMultiSelect from "src/Manager/Campaigns/Segments/CreateNewSegmentDialog/AlgoliaMultiSelect";
import SegmentSearchField from "../SegmentSearchField";
import KydAudienceOrDivider from "../KydAudienceOrDivider";
import KydAudiencePanelCard from "../KydAudiencePanelCard";
import { KYD_AUDIENCE_DIALOG_STYLES } from "../kydAudienceDialogStyles";
import { StepProps, CreateCustomAudienceFormValues } from "../types";

const styles = KYD_AUDIENCE_DIALOG_STYLES;

const BasicInfoStep: React.FC<StepProps> = ({
  eventsAlgoliaConfig,
  segmentsAlgoliaConfig,
}) => {
  const form = useFormContext<CreateCustomAudienceFormValues>();
  const segmentSelected = Boolean(form.watch("segment")?.value);
  const filtersDisabled = segmentSelected;

  return (
    <VStack spacing={6} align="stretch" fontFamily={styles.fontFamily}>
      <KYDInput
        variant="labeled"
        label="Audience name"
        placeholder="Enter audience name"
        value={form.watch("name")}
        onChange={(e) => form.setValue("name", e.target.value)}
      />

      {segmentsAlgoliaConfig ? (
        <KydAudiencePanelCard>
          <VStack align="stretch" spacing={`${styles.panelGapPx}px`}>
            <Text
              fontSize={styles.sectionLabelFontSize}
              fontWeight={styles.sectionLabelFontWeight}
              color={styles.sectionLabelColor}
              lineHeight={1.2}
            >
              Build an audience from an existing segment
            </Text>
            <InstantSearch
              searchClient={segmentsAlgoliaConfig.searchClient}
              indexName={segmentsAlgoliaConfig.indexName}
            >
              <SegmentSearchField />
            </InstantSearch>
          </VStack>
        </KydAudiencePanelCard>
      ) : null}

      <KydAudienceOrDivider />

      <KydAudiencePanelCard>
        <VStack
          align="stretch"
          spacing={`${styles.filtersPanelGapPx}px`}
          opacity={filtersDisabled ? 0.5 : 1}
          pointerEvents={filtersDisabled ? "none" : "auto"}
        >
          {filtersDisabled ? (
            <Text
              fontSize={styles.disabledHintFontSize}
              fontWeight={styles.sectionLabelFontWeight}
              color={styles.disabledHintColor}
              lineHeight={1.2}
            >
              This option is disabled when using an existing segment
            </Text>
          ) : null}

          <VStack align="stretch" spacing={`${styles.panelGapPx}px`}>
            <Text
              fontSize={styles.sectionLabelFontSize}
              fontWeight={styles.sectionLabelFontWeight}
              color={styles.sectionLabelColor}
              lineHeight={1.2}
            >
              People who&apos;ve attended events in these genres:
            </Text>
            <KYDMultiSelect
              id="genre"
              placeholder="Search or select multiple genres"
              options={KYDGenresList.map((item) => ({
                label: item.label,
                value: item.name,
              }))}
              values={form.watch("genres")}
              isDisabled={filtersDisabled}
              onChange={(vals) => {
                form.setValue("genres", vals, { shouldDirty: true });
                if (vals.length > 0) {
                  form.setValue("segment", null);
                  form.setValue("segment_size", undefined);
                }
              }}
            />
          </VStack>

          {eventsAlgoliaConfig ? (
            <VStack align="stretch" spacing={`${styles.panelGapPx}px`}>
              <Text
                fontSize={styles.sectionLabelFontSize}
                fontWeight={styles.sectionLabelFontWeight}
                color={styles.sectionLabelColor}
                lineHeight={1.2}
              >
                People who&apos;ve attended events with these artists in
                line-up:
              </Text>
              <InstantSearch
                searchClient={eventsAlgoliaConfig.searchClient}
                indexName={eventsAlgoliaConfig.indexName}
              >
                <AlgoliaMultiSelect
                  searchKey="performers.name"
                  label=""
                  placeholder="Search for artists by name or keyword"
                  values={form.watch("artists")}
                  onChange={(vals) => {
                    form.setValue("artists", vals, { shouldDirty: true });
                    if (vals.length > 0) {
                      form.setValue("segment", null);
                      form.setValue("segment_size", undefined);
                    }
                  }}
                />
              </InstantSearch>
            </VStack>
          ) : null}
        </VStack>
      </KydAudiencePanelCard>
    </VStack>
  );
};

export default BasicInfoStep;
